import { useQuery } from '@tanstack/react-query';
import { Trophy, Medal, Flame, Target, TrendingUp, Award } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

interface GamificationOverviewProps {
  companyId: string;
  settings: any;
}

export function GamificationOverview({ companyId, settings }: GamificationOverviewProps) {
  // Fetch actual team member count from profiles
  const { data: teamMemberCount } = useQuery({
    queryKey: ['team-member-count', companyId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId);
      
      if (error) {
        console.error('Error fetching team count:', error);
        return 0;
      }
      return count || 0;
    },
    enabled: !!companyId,
  });

  // Fetch team stats from evaluations
  const { data: teamStats } = useQuery({
    queryKey: ['team-gamification-stats', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('simulation_evaluations')
        .select('user_id, overall_score')
        .eq('company_id', companyId)
        .eq('is_submitted', true)
        .is('deleted_at', null);
      
      if (error) {
        console.error('Error fetching stats:', error);
        return { totalSessions: 0, avgScore: 0, uniqueUsers: 0 };
      }
      
      const uniqueUsers = new Set(data?.map(d => d.user_id)).size;
      const totalSessions = data?.length || 0;
      const avgScore = data?.length 
        ? Math.round(data.reduce((sum, d) => sum + (d.overall_score || 0), 0) / data.length)
        : 0;
      
      return { totalSessions, avgScore, uniqueUsers };
    },
    enabled: !!companyId,
  });

  // Fetch badges stats
  const { data: badgeStats } = useQuery({
    queryKey: ['team-badge-stats', companyId],
    queryFn: async () => {
      const { data: badges } = await supabase
        .from('company_badges')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('is_active', true);
      
      const { data: earned } = await supabase
        .from('user_company_badges')
        .select('badge_id')
        .eq('company_id', companyId);
      
      const badgeCounts = new Map<string, number>();
      earned?.forEach(e => {
        badgeCounts.set(e.badge_id, (badgeCounts.get(e.badge_id) || 0) + 1);
      });
      
      return {
        totalBadges: badges?.length || 0,
        totalEarned: earned?.length || 0,
        badgeDistribution: badges?.map(b => ({
          name: b.name,
          count: badgeCounts.get(b.id) || 0,
        })) || [],
      };
    },
    enabled: !!companyId,
  });

  // Fetch milestones stats
  const { data: milestoneStats } = useQuery({
    queryKey: ['team-milestone-stats', companyId],
    queryFn: async () => {
      const { data: milestones } = await supabase
        .from('company_milestones')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_active', true);
      
      return {
        totalMilestones: milestones?.length || 0,
        completedMilestones: 0, // Will be tracked via user_milestone_progress
      };
    },
    enabled: !!companyId,
  });

  // Fetch top performers with profile fallback for missing names
  const { data: leaderboard = [] } = useQuery({
    queryKey: ['performance-leaderboard', companyId],
    queryFn: async () => {
      const { data: cacheData } = await supabase
        .from('team_performance_cache')
        .select('user_id, user_name, total_sessions, average_score')
        .eq('company_id', companyId)
        .order('total_sessions', { ascending: false })
        .limit(5);

      if (!cacheData || cacheData.length === 0) {
        return [];
      }

      // Find users with missing names
      const missingNameUserIds = cacheData
        .filter(d => !d.user_name || d.user_name === 'Unknown')
        .map(d => d.user_id)
        .filter(Boolean);

      // Fetch profile names for users with missing names
      let profileMap: Record<string, string> = {};
      if (missingNameUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, email')
          .in('id', missingNameUserIds);

        profiles?.forEach(p => {
          profileMap[p.id] = p.display_name || p.email?.split('@')[0] || 'User';
        });
      }
      
      return cacheData.map((item, index) => ({
        rank: index + 1,
        name: item.user_name && item.user_name !== 'Unknown' 
          ? item.user_name 
          : profileMap[item.user_id] || 'User',
        sessions: item.total_sessions || 0,
      }));
    },
    enabled: !!companyId,
  });

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--success))', 'hsl(var(--accent))', 'hsl(var(--warning))', 'hsl(var(--destructive))'];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Trophy className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Sessions</p>
                <p className="text-2xl font-bold">{teamStats?.totalSessions.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-success/10">
                <Medal className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Badges Earned</p>
                <p className="text-2xl font-bold">{badgeStats?.totalEarned || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-warning/10">
                <Flame className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Team Members</p>
                <p className="text-2xl font-bold">{teamMemberCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-accent/10">
                <Target className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Score</p>
                <p className="text-2xl font-bold">{teamStats?.avgScore || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Badge Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Medal className="h-5 w-5" />
              Badge Distribution
            </CardTitle>
            <CardDescription>Which badges are most earned by your team</CardDescription>
          </CardHeader>
          <CardContent>
            {badgeStats?.badgeDistribution && badgeStats.badgeDistribution.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={badgeStats.badgeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, count }) => `${name}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {badgeStats.badgeDistribution.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No badges configured yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Performers
            </CardTitle>
            <CardDescription>Most active team members by sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leaderboard} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="sessions" fill="hsl(var(--primary))" radius={4} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No CPD data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Gamification Summary
          </CardTitle>
          <CardDescription>
            Overview of team engagement and achievements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-primary">{badgeStats?.totalBadges || 0}</p>
              <p className="text-sm text-muted-foreground">Active Badges</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-success">{milestoneStats?.totalMilestones || 0}</p>
              <p className="text-sm text-muted-foreground">Active Milestones</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-accent">{teamStats?.avgScore || 0}%</p>
              <p className="text-sm text-muted-foreground">Avg Team Score</p>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
